<script lang="ts">
    import { onMount } from "svelte";
    import type { IServices } from "./code/services";

    import type { IInvoiceData } from "./code/backend/admin.generated.types";
    import { niceDate, niceNumber, UrlHelper, CssHelpers } from "./code/utils";
    import JobStatusPanel from "./JobStatusPanel.svelte";
    import Loader from "./Loader.svelte";
    import LoaderSmall from "./LoaderSmall.svelte";

    export let services: IServices;
    export const page_params: any = {};

    let invoices: IInvoiceData2[] = [];
    let is_loading: boolean = false;
    let is_rerendering_invoice: boolean = false;

    let job_uuid: string = "";
    let job_title: string = "";

    let month_items: { month: string; offset: number }[] = [];
    let selected_month_item_offset: number = 0;
    let show_download_panel: boolean = false;
    let show_downloading_loader: boolean = false;

    interface IInvoiceData2 extends IInvoiceData {
        amount_formatted: string;
        amount_net_formatted: string;
        amount_tax_formatted: string;
        invoice_date_formatted: string;
    }

    function transform(x: IInvoiceData): IInvoiceData2 {
        return {
            ...x,
            amount_formatted: niceNumber(x.amount, 2),
            amount_net_formatted: niceNumber(x.amount_net, 4),
            amount_tax_formatted: niceNumber(x.amount_tax, 2),
            invoice_date_formatted: niceDate(x.invoice_date),
        };
    }

    async function load(): Promise<void> {
        is_loading = true;
        invoices = (await services.backend.admin().getInvoices({})).invoices
            .map((x) => transform(x))
            .sort((a, b) => {
                // sort by date, then by id (descending)
                if (a.invoice_date != b.invoice_date) {
                    return b.invoice_date - a.invoice_date;
                }
                return (b.invoice_number ?? "").localeCompare(
                    a.invoice_number ?? "",
                );
            });
        is_loading = false;
    }

    async function openInvoice(external_id1: string): Promise<void> {
        const invoice = await services.backend
            .reservations()
            .downloadInvoice({ id: external_id1 });
        UrlHelper.openFileForToken(invoice.token);
    }

    async function rerenderInvoice(invoice: IInvoiceData): Promise<void> {
        if (!confirm("Ali želite osvežiti račun v hrambi?")) {
            return;
        }
        is_rerendering_invoice = true;
        try {
            await services.backend
                .admin()
                .rerenderSingleInvoice({ id: invoice.uuid });
            await load();
            is_rerendering_invoice = false;
        } catch (e) {
            is_rerendering_invoice = false;
            alert(e.message);
        }
    }

    async function infoRenderInvoice(invoice: IInvoiceData): Promise<void> {
        is_rerendering_invoice = true;
        try {
            const { html } = await services.backend
                .admin()
                .infoRenderSingleInvoice({ id: invoice.uuid });
            // show html in a new tab
            const new_tab = window.open("", "_blank");
            if (new_tab != null) {
                new_tab.document.write(html);
            }
            is_rerendering_invoice = false;
        } catch (e) {
            is_rerendering_invoice = false;
            alert(e.message);
        }
    }

    async function infoRenderProformaEmailPreview(invoice: IInvoiceData): Promise<void> {
        is_rerendering_invoice = true;
        try {
            const { html, subject } = await services.backend
                .admin()
                .infoRenderProformaEmail({ id: invoice.uuid });
            const new_tab = window.open("", "_blank");
            if (new_tab != null) {
                const subject_header = `<p style="margin:0 0 1em 0;font-size:12px;color:#666;">Predmet: ${subject}</p>`;
                new_tab.document.write(subject_header + html);
            }
            is_rerendering_invoice = false;
        } catch (e) {
            is_rerendering_invoice = false;
            alert(e.message);
        }
    }

    async function deleteInvoice(invoice: IInvoiceData): Promise<void> {
        is_rerendering_invoice = true;
        try {
            await services.backend.admin().deleteInvoice({ id: invoice.uuid });
            await load();
            is_rerendering_invoice = false;
        } catch (e) {
            is_rerendering_invoice = false;
            alert(e.message);
        }
    }

    function prepareMonths(): void {
        /** Month names */
        const month_names = [
            "Januar",
            "Februar",
            "Marec",
            "April",
            "Maj",
            "Junij",
            "Julij",
            "Avgust",
            "September",
            "Oktober",
            "November",
            "December",
        ];
        /** Zero-index-based month */
        let curr_month = new Date().getMonth();
        let curr_year = new Date().getFullYear();
        let tmp_month_items = month_items.slice(0, 0);
        let offset = 0;
        for (let i = 0; i < 12; i++) {
            tmp_month_items.push({
                month: month_names[curr_month] + " " + curr_year,
                offset,
            });
            curr_month--;
            if (curr_month < 0) {
                curr_month = 11;
                curr_year--;
            }
            offset--;
        }
        month_items = tmp_month_items;
        selected_month_item_offset = 0;
    }
    onMount(async () => {
        prepareMonths();
        await load();
    });

    async function onGenerateNew(): Promise<void> {
        const res = await services.backend.admin().prepareNewInvoices({});
        job_uuid = res.job_uuid;
        job_title = "Generiranje faktur";
    }

    async function onRenderMissing(): Promise<void> {
        const res = await services.backend.admin().renderMissingInvoices({});
        job_uuid = res.job_uuid;
        job_title = "Render manjkajočih faktur";
    }

    async function onFinalizePending(): Promise<void> {
        // const this_year = new Date().getFullYear();
        // const next_invoice1 = await services.backend
        //     .admin()
        //     .getNextInvoiceNumberForYear({ year: this_year });
        // const next_invoice2 = await services.backend
        //     .admin()
        //     .getNextInvoiceNumberForYear({ year: this_year - 1 });

        // const ok = confirm(
        //     `Naslednji račun za letos je ${next_invoice1.next_invoice_number}, za lani ${next_invoice2.next_invoice_number}.`
        // );
        // if (!ok) {
        //     return;
        // }

        const num_s = prompt(
            "Vnesi prvo številko računa, ki bo sedaj izdan (brez letnice, samo številka):",
        );
        if (num_s == null) {
            return;
        }
        if (!/^\d+$/gm.test(num_s)) {
            alert("Napaka: vnesena številka ni število.");
            return;
        }

        const res = await services.backend.admin().finalizeInvoices({
            num: parseInt(num_s, 10),
        });
        job_uuid = res.job_uuid;
        job_title = "Finaliziranje faktur";
    }

    async function onDownloadCsv2(type: "pre-vat" | "for-vat"): Promise<void> {
        await onDownloadCsv(selected_month_item_offset, type);
    }
    async function onDownloadZip2(): Promise<void> {
        await onDownloadZip(selected_month_item_offset);
    }
    async function onDownloadCsv(
        month_offset: number,
        type: "pre-vat" | "for-vat",
    ): Promise<void> {
        show_downloading_loader = true;
        try {
            const res = await services.backend
                .admin()
                .downloadCsvOfInvoices({ month_offset, type });
            UrlHelper.openFileForToken(res.token);
        } finally {
            show_downloading_loader = false;
        }
    }
    async function onDownloadZip(month_offset: number): Promise<void> {
        show_downloading_loader = true;
        try {
            const res = await services.backend
                .admin()
                .downloadBatchOfInvoices({ month_offset });
            UrlHelper.openFileForToken(res.token);
        } finally {
            show_downloading_loader = false;
        }
    }
</script>

<div class="w3-container">
    <div class="w3-row">
        <button class="w3-button-link w3-right" on:click={() => load()}>
            Osveži
        </button>
        <button class="w3-button-link" on:click={() => onGenerateNew()}>
            Generiraj nove
        </button>
        <button class="w3-button-link" on:click={() => onFinalizePending()}>
            Finaliziraj
        </button>
        <button class="w3-button-link" on:click={() => onRenderMissing()}>
            Renderiraj manjkajoče
        </button>
        &bullet;
        <!-- <button class="w3-button-link" on:click={() => onDownloadCsv(0)}>
            Zadnji podatki
        </button>
        <button class="w3-button-link" on:click={() => onDownloadCsv(-1)}>
            Predzadnji podatki
        </button>
        &bullet;
        <button class="w3-button-link" on:click={() => onDownloadZip(0)}>
            Zadnji ZIP
        </button>
        <button class="w3-button-link" on:click={() => onDownloadZip(-1)}>
            Predzadnji ZIP
        </button> -->
        <button
            class="w3-button-link"
            on:click={() => {
                show_download_panel = true;
            }}
        >
            Prenesi datoteke
        </button>
    </div>
    {#if show_download_panel}
        <div class="w3-row">
            Mesec:
            <select
                bind:value={selected_month_item_offset}
                class="w3-select w3-border w3-round"
            >
                {#each month_items as month_item}
                    <option value={month_item.offset}>
                        {month_item.month}
                    </option>
                {/each}
            </select>
            <button
                class="w3-button-link"
                on:click={() => onDownloadCsv2("pre-vat")}
            >
                Prenesi podatke
            </button>
            <button
                class="w3-button-link"
                on:click={() => onDownloadCsv2("for-vat")}
            >
                Prenesi podatke novi
            </button>
            <button class="w3-button-link" on:click={() => onDownloadZip2()}>
                Prenesi ZIP
            </button>
            <LoaderSmall visible={show_downloading_loader} />
        </div>
    {/if}
    <div class="w3-row">
        <div class="w3-col m12 l12 w3-padding-large w3-center">
            <JobStatusPanel {services} uuid={job_uuid} title={job_title} />
            <div class="w3-center">
                <Loader visible={is_loading && is_rerendering_invoice} />
            </div>
            {#if !is_loading}
                <table class="w3-table-all w3-hoverable">
                    <tr>
                        <th>Id</th>
                        <th>Status</th>
                        <th>Datum</th>
                        <th style="min-width: 120px">Račun</th>
                        <th>Znesek</th>
                        <th>Osnova DDV</th>
                        <th>DDV</th>
                        <th>Uporabnik</th>
                        <th>Naziv</th>
                        <th>&nbsp;</th>
                    </tr>
                    {#each invoices as invoice}
                        <tr>
                            <td>
                                {invoice.uuid}
                            </td>
                            <td>
                                <span
                                    class="w3-tag w3-round-large {CssHelpers.getCssClassForInvoice(
                                        invoice.finalized,
                                        invoice.external_id2,
                                    )}"
                                    >{CssHelpers.getTagTitleForInvoice(
                                        invoice.finalized,
                                        invoice.external_id2,
                                    )}</span
                                >
                            </td>
                            <td class="w3-right-align">
                                {invoice.invoice_date_formatted}
                            </td>
                            <td>
                                <button
                                    on:click={() =>
                                        openInvoice(invoice.external_id1)}
                                    class="w3-button-link"
                                    >{invoice.invoice_number}</button
                                >
                            </td>
                            <td class="w3-right-align">
                                {invoice.amount_formatted}
                            </td>
                            <td class="w3-right-align">
                                {invoice.amount_net_formatted}
                            </td>
                            <td class="w3-right-align">
                                {invoice.amount_tax_formatted}
                            </td>
                            <td>
                                <a
                                    href={UrlHelper.getOpenUserUrl(
                                        invoice.user_uuid,
                                    )}
                                >
                                    {invoice.username}
                                </a>
                            </td>
                            <td>
                                {invoice.user_title}
                            </td>
                            <td>
                                {#if invoice.external_id2 != null}
                                    <button
                                        on:click={() => rerenderInvoice(invoice)}
                                        class="w3-button-link"
                                        title="Osveži račun"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 30 30">
                                            <path d="M 15 3 C 12.031398 3 9.3028202 4.0834384 7.2070312 5.875 A 1.0001 1.0001 0 1 0 8.5058594 7.3945312 C 10.25407 5.9000929 12.516602 5 15 5 C 20.19656 5 24.450989 8.9379267 24.951172 14 L 22 14 L 26 20 L 30 14 L 26.949219 14 C 26.437925 7.8516588 21.277839 3 15 3 z M 4 10 L 0 16 L 3.0507812 16 C 3.562075 22.148341 8.7221607 27 15 27 C 17.968602 27 20.69718 25.916562 22.792969 24.125 A 1.0001 1.0001 0 1 0 21.494141 22.605469 C 19.74593 24.099907 17.483398 25 15 25 C 9.80344 25 5.5490109 21.062074 5.0488281 16 L 8 16 L 4 10 z"></path>
                                        </svg>
                                    </button>
                                {/if}
                                {#if invoice.external_id2 === null}
                                    <button
                                        on:click={() => deleteInvoice(invoice)}
                                        class="w3-button-link"
                                        title="Zbriši račun"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 30 30">
                                            <path d="M 14.984375 2.4863281 A 1.0001 1.0001 0 0 0 14 3.5 L 14 4 L 8.5 4 A 1.0001 1.0001 0 0 0 7.4863281 5 L 6 5 A 1.0001 1.0001 0 1 0 6 7 L 24 7 A 1.0001 1.0001 0 1 0 24 5 L 22.513672 5 A 1.0001 1.0001 0 0 0 21.5 4 L 16 4 L 16 3.5 A 1.0001 1.0001 0 0 0 14.984375 2.4863281 z M 6 9 L 7.7929688 24.234375 C 7.9109687 25.241375 8.7633438 26 9.7773438 26 L 20.222656 26 C 21.236656 26 22.088031 25.241375 22.207031 24.234375 L 24 9 L 6 9 z"></path>
                                        </svg>
                                    </button>
                                {/if}
                                <button
                                    on:click={() => infoRenderInvoice(invoice)}
                                    class="w3-button-link"
                                    title="Prikaži informativni račun"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 50 50">
                                        <path d="M 25 2 C 12.309295 2 2 12.309295 2 25 C 2 37.690705 12.309295 48 25 48 C 37.690705 48 48 37.690705 48 25 C 48 12.309295 37.690705 2 25 2 z M 25 4 C 36.609824 4 46 13.390176 46 25 C 46 36.609824 36.609824 46 25 46 C 13.390176 46 4 36.609824 4 25 C 4 13.390176 13.390176 4 25 4 z M 25 11 A 3 3 0 0 0 22 14 A 3 3 0 0 0 25 17 A 3 3 0 0 0 28 14 A 3 3 0 0 0 25 11 z M 21 21 L 21 23 L 22 23 L 23 23 L 23 36 L 22 36 L 21 36 L 21 38 L 22 38 L 23 38 L 27 38 L 28 38 L 29 38 L 29 36 L 28 36 L 27 36 L 27 21 L 26 21 L 22 21 L 21 21 z"></path>
                                    </svg>
                                </button>
                                <button
                                    on:click={() => infoRenderProformaEmailPreview(invoice)}
                                    class="w3-button-link"
                                    title="Prikaži vsebino e-pošte (proforma)"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                </button>
                            </td>
                        </tr>
                    {/each}
                </table>
            {/if}
        </div>
    </div>
</div>
